<template>
  <div class="page post" v-if="post">
    <section class="post-hero">
      <nav class="crumb">
        <router-link to="/home">首页</router-link>
        <span>/</span>
        <router-link to="/insights">行业洞察</router-link>
        <span>/</span>
        <em>{{ post.category }}</em>
      </nav>
      <span class="eyebrow reveal" v-reveal>{{ post.category }}</span>
      <h1 class="h2 post-title reveal" v-reveal="{ delay: 60 }">{{ post.title }}</h1>
      <div class="post-meta reveal" v-reveal="{ delay: 120 }">
        <span>{{ post.date }}</span>
        <i></i>
        <span>阅读约 {{ post.minutes }} 分钟</span>
      </div>
    </section>

    <section class="post-layout">
      <article class="post-body" v-html="post.html"></article>

      <aside class="post-side">
        <div class="side-card card card-pad">
          <h3 class="side-title">相关解决方案</h3>
          <router-link :to="line.path" class="side-line">
            <span>{{ line.name }}</span>
            <IconSvg name="arrow-right"/>
          </router-link>
          <p class="side-desc">{{ line.tagline }}</p>
          <router-link to="/contact" class="btn btn-primary side-btn">
            沟通你的场景
            <IconSvg name="arrow-right"/>
          </router-link>
        </div>

        <div class="side-card card card-pad">
          <h3 class="side-title">关键词</h3>
          <div class="side-tags">
            <span v-for="k in post.keywords" :key="k">{{ k }}</span>
          </div>
        </div>

        <div class="side-card card card-pad" v-if="related.length">
          <h3 class="side-title">继续阅读</h3>
          <router-link
            v-for="r in related"
            :key="r.slug"
            :to="`/insights/${r.slug}`"
            class="side-post"
          >
            <span class="side-post-title">{{ r.title }}</span>
            <span class="side-post-min">{{ r.minutes }} 分钟</span>
          </router-link>
        </div>
      </aside>
    </section>

    <CtaBand
      :eyebrow="line.en"
      :title="ctaTitle"
      :desc="ctaDesc"
    />
  </div>
</template>

<script>
import IconSvg from '@/components/common/IconSvg'
import CtaBand from '@/components/common/CtaBand'
import { posts, getPost } from '@/data/posts'
import { businessLines, company } from '@/data/site'

const SITE = 'https://www.hoyotech.top'
const LD_ID = 'ld-article'

/** 文章页：按 slug 渲染站内长文，并注入 Article / BreadcrumbList 结构化数据 */
export default {
  name: 'PostPage',
  components: { IconSvg, CtaBand },
  data() {
    return { posts, company }
  },
  computed: {
    post() {
      return getPost(this.$route.params.slug)
    },
    line() {
      const l = businessLines.find((x) => x.path === (this.post && this.post.line))
      return l || businessLines[0]
    },
    related() {
      if (!this.post) return []
      return this.posts
        .filter((p) => p.categoryKey === this.post.categoryKey && p.slug !== this.post.slug)
        .slice(0, 3)
    },
    ctaTitle() {
      return this.line ? `把${this.line.name}落到你的现场` : '把你的场景讲给我们听'
    },
    ctaDesc() {
      return (this.line && this.line.summary) ||
        '从现场勘查到方案落地，皓辰提供一对一技术沟通，先判断可行性，再谈合作方式。'
    }
  },
  watch: {
    $route() {
      this.sync()
    }
  },
  mounted() {
    this.sync()
  },
  beforeDestroy() {
    const el = document.getElementById(LD_ID)
    if (el) el.remove()
  },
  methods: {
    /**
     * 组件自己负责本页的 title / description / 结构化数据。
     * 路由的 afterEach 对 name==='post' 直接跳过，避免被静态 meta 覆盖。
     */
    sync() {
      const p = this.post
      if (!p) return
      const url = `${SITE}/insights/${p.slug}`

      document.title = `${p.title} · 皓辰科技 HOYOTECH`
      let d = document.querySelector('meta[name="description"]')
      if (!d) {
        d = document.createElement('meta')
        d.setAttribute('name', 'description')
        document.head.appendChild(d)
      }
      d.setAttribute('content', p.description)

      const ld = {
        '@context': 'https://schema.org',
        '@graph': [
          {
            '@type': 'Article',
            headline: p.title,
            description: p.description,
            datePublished: p.date,
            dateModified: p.date,
            inLanguage: 'zh-CN',
            articleSection: p.category,
            keywords: p.keywords.join(','),
            author: { '@type': 'Organization', name: company.name },
            publisher: {
              '@type': 'Organization',
              name: company.name,
              logo: { '@type': 'ImageObject', url: `${SITE}/logo.png` }
            },
            mainEntityOfPage: { '@type': 'WebPage', '@id': url }
          },
          {
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: `${SITE}/home` },
              { '@type': 'ListItem', position: 2, name: '行业洞察', item: `${SITE}/insights` },
              { '@type': 'ListItem', position: 3, name: p.title, item: url }
            ]
          }
        ]
      }

      let el = document.getElementById(LD_ID)
      if (!el) {
        el = document.createElement('script')
        el.type = 'application/ld+json'
        el.id = LD_ID
        document.head.appendChild(el)
      }
      el.textContent = JSON.stringify(ld)
    }
  }
}
</script>

<style lang="scss" scoped>
.post { padding-top: 132px; padding-bottom: 40px; }

.post-hero {
  max-width: 820px;
  margin-bottom: 44px;

  .crumb {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    margin-bottom: 18px;
    font-size: 13px;
    color: var(--txt-3);
    a { color: var(--txt-2); }
    a:hover { color: var(--cyan); }
    em { font-style: normal; color: var(--txt-2); }
  }
}

.post-title { margin: 10px 0 16px; line-height: 1.45; }

.post-meta {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 13px;
  color: var(--txt-3);
  i { width: 1px; height: 12px; background: var(--line-strong); }
}

.post-layout {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 320px;
  gap: 46px;
  align-items: start;
}

/* 正文排版：与全站一套变量，长文可读性优先 */
.post-body {
  max-width: 780px;
  font-size: 15.4px;
  line-height: 1.95;
  color: var(--txt-2);

  ::v-deep h2 {
    position: relative;
    margin: 46px 0 16px;
    padding-left: 14px;
    font-size: 21px;
    font-weight: 700;
    line-height: 1.5;
    color: #fff;

    &::before {
      content: "";
      position: absolute;
      left: 0;
      top: 0.34em;
      width: 3px;
      height: 1.05em;
      border-radius: 2px;
      background: linear-gradient(180deg, var(--cyan), var(--blue));
    }
    &:first-child { margin-top: 0; }
  }

  ::v-deep h3 {
    margin: 30px 0 12px;
    font-size: 17px;
    font-weight: 700;
    color: #fff;
  }

  ::v-deep p { margin: 0 0 16px; }

  ::v-deep strong { color: #fff; font-weight: 600; }

  ::v-deep a {
    color: var(--cyan);
    border-bottom: 1px solid rgba(34, 224, 255, 0.35);
    &:hover { border-bottom-color: var(--cyan); }
  }

  ::v-deep ul, ::v-deep ol {
    margin: 0 0 18px;
    padding-left: 0;
    list-style: none;

    li {
      position: relative;
      padding-left: 20px;
      margin-bottom: 9px;

      &::before {
        content: "";
        position: absolute;
        left: 2px;
        top: 0.72em;
        width: 6px;
        height: 6px;
        border-radius: 50%;
        background: var(--cyan);
        box-shadow: 0 0 8px var(--cyan);
      }
    }
  }

  ::v-deep ol { counter-reset: step; }

  ::v-deep blockquote {
    margin: 22px 0;
    padding: 14px 20px;
    border-left: 3px solid var(--cyan);
    border-radius: 0 12px 12px 0;
    background: rgba(34, 224, 255, 0.06);
    color: var(--txt);

    p:last-child { margin-bottom: 0; }
  }

  ::v-deep table {
    width: 100%;
    margin: 24px 0;
    border-collapse: collapse;
    font-size: 14px;
    border: 1px solid var(--line);
    border-radius: 12px;
    overflow: hidden;

    th, td {
      padding: 11px 14px;
      text-align: left;
      border-bottom: 1px solid var(--line);
      vertical-align: top;
    }

    th {
      color: #fff;
      font-weight: 600;
      background: rgba(34, 224, 255, 0.10);
      white-space: nowrap;
    }

    tr:last-child td { border-bottom: 0; }
    tbody tr:hover td { background: rgba(255, 255, 255, 0.02); }
  }

  ::v-deep hr {
    margin: 34px 0;
    border: 0;
    border-top: 1px solid var(--line);
  }

  ::v-deep code {
    padding: 2px 6px;
    border-radius: 6px;
    font-size: 13.5px;
    background: rgba(255, 255, 255, 0.06);
    color: var(--accent-txt);
  }
}

.post-side {
  position: sticky;
  top: calc(var(--nav-h) + 20px);
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.side-card { border-radius: 16px; }

.side-title {
  margin: 0 0 14px;
  font-size: 14px;
  font-weight: 700;
  color: #fff;
  letter-spacing: 0.02em;
}

.side-line {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 12px 14px;
  border-radius: 12px;
  border: 1px solid var(--line);
  background: rgba(255, 255, 255, 0.03);
  color: #fff;
  font-size: 14.5px;
  transition: border-color 0.35s, color 0.35s;

  svg { width: 16px; height: 16px; color: var(--cyan); }
  &:hover { border-color: var(--line-strong); color: var(--cyan); }
}

.side-desc {
  margin: 12px 0 16px;
  font-size: 13px;
  line-height: 1.8;
  color: var(--txt-3);
}

.side-btn { width: 100%; justify-content: center; height: 44px; font-size: 14px; }

.side-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;

  span {
    padding: 5px 10px;
    border-radius: 8px;
    font-size: 12.5px;
    color: var(--txt-2);
    background: rgba(255, 255, 255, 0.04);
    border: 1px solid var(--line);
  }
}

.side-post {
  display: block;
  padding: 11px 0;
  border-bottom: 1px solid rgba(120, 176, 255, 0.1);
  &:last-child { border-bottom: 0; padding-bottom: 0; }

  .side-post-title {
    display: block;
    font-size: 13.5px;
    line-height: 1.7;
    color: var(--txt-2);
  }
  .side-post-min { display: block; margin-top: 5px; font-size: 12px; color: var(--txt-3); }
  &:hover .side-post-title { color: var(--cyan); }
}

@media (max-width: 1080px) {
  .post-layout { grid-template-columns: minmax(0, 1fr); gap: 34px; }
  .post-side { position: static; }
}

@media (max-width: 760px) {
  .post { padding-top: 108px; }
  .post-body { font-size: 15px; }
  .post-body ::v-deep table { font-size: 13px; }
  .post-body ::v-deep th, .post-body ::v-deep td { padding: 9px 11px; }
}
</style>
